import { PlayCircleIcon, QuestionMarkCircleIcon, TrophyIcon } from '@heroicons/react/24/outline'
import Icon from './Icon'
import { EXTENSION_URLS } from '../utils/constants'
import { PageLayout } from './PageLayout'

const statusColors: Record<string, string> = {
  saved: 'bg-app-page text-app-ink border-app-line',
  applied: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-700',
  in_process: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700',
  offer: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
  accepted: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
  offer_declined: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
  ghosted: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
  withdrawn: 'bg-app-page text-app-muted border-app-line',
  rejected: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
  hired: 'bg-emerald-200 dark:bg-emerald-800/60 text-emerald-800 dark:text-emerald-200 border-emerald-400 dark:border-emerald-600',
  archived: 'bg-app-subtle text-app-muted border-app-line',
}

const StatusBadge = ({ status, label }: { status: string; label: string }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusColors[status]}`}>
    {label}
  </span>
)

type HelpPageProps = {
  onStartProductTour: () => void
}

export const HelpPage = ({ onStartProductTour }: HelpPageProps) => {
  return (
    <PageLayout
      icon={QuestionMarkCircleIcon}
      title="Help & Documentation"
      subtitle="Learn how JobTrackr works"
    >
      <div className="card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-app-ink font-display">Interactive product tour</h2>
            <p className="mt-1 text-sm text-app-muted">
              Revisit the guided walkthrough anytime from here.
            </p>
          </div>
          <button
            type="button"
            onClick={onStartProductTour}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-app-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-app-primary-hover"
          >
            <PlayCircleIcon className="h-5 w-5" />
            Start tour
          </button>
        </div>
      </div>

      {/* Application State Machine */}
      <div className="card p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-app-ink mb-2 font-display">Application Status Flow</h2>
          <p className="text-sm text-app-muted">
            Each job application follows a state machine that tracks your progress through the hiring process.
            Below is a table showing available actions from each status.
          </p>
        </div>

        {/* Status Actions Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-app-line">
                <th className="text-left py-3 px-4 font-semibold text-app-ink">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-app-ink">Available Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-line">
              <tr>
                <td className="py-3 px-4"><StatusBadge status="saved" label="Saved" /></td>
                <td className="py-3 px-4 text-app-muted">Mark Applied, Start Process, Withdraw</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="applied" label="Applied" /></td>
                <td className="py-3 px-4 text-app-muted">Undo → Saved, Start Process, Ghosted, Withdraw, Reject</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="in_process" label="In Progress" /></td>
                <td className="py-3 px-4 text-app-muted">Undo → Applied, Got Offer, Ghosted, Withdraw, Reject</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="offer" label="Offer" /></td>
                <td className="py-3 px-4 text-app-muted">Undo → In Process, Accept, Decline, Reject</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="accepted" label="Accepted" /></td>
                <td className="py-3 px-4 text-app-muted">Undo → Offer, Mark Hired</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="offer_declined" label="Declined" /></td>
                <td className="py-3 px-4 text-app-muted">Undo → Offer (changed mind)</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="ghosted" label="Ghosted" /></td>
                <td className="py-3 px-4 text-app-muted">Resume → In Process, Withdraw, Reject</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="rejected" label="Rejected" /></td>
                <td className="py-3 px-4 text-app-muted">Resume → In Process (company reconsidered)</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="withdrawn" label="Withdrawn" /></td>
                <td className="py-3 px-4 text-app-muted italic">— Terminal (no further actions)</td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="hired" label="Hired" /></td>
                <td className="py-3 px-4 text-app-muted italic flex items-center gap-1">— Terminal <TrophyIcon className="h-4 w-4 text-amber-500 inline" /></td>
              </tr>
              <tr>
                <td className="py-3 px-4"><StatusBadge status="archived" label="Archived" /></td>
                <td className="py-3 px-4 text-app-muted italic">— Special (any status can be archived/unarchived)</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Status Descriptions */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <h3 className="font-semibold text-app-ink font-display">Active Statuses</h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-start gap-2">
                <StatusBadge status="saved" label="Saved" />
                <span className="text-app-muted">Job saved for later — not yet applied</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="applied" label="Applied" />
                <span className="text-app-muted">Application submitted — waiting for response</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="in_process" label="In Progress" />
                <span className="text-app-muted">Actively interviewing with the company</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="offer" label="Offer" />
                <span className="text-app-muted">Received an offer — decision pending</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="accepted" label="Accepted" />
                <span className="text-app-muted">Offer accepted — finalizing details</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-app-ink font-display">Outcome Statuses</h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-start gap-2">
                <StatusBadge status="hired" label="Hired" />
                <span className="text-app-muted flex items-center gap-1">Successfully hired <TrophyIcon className="h-4 w-4 text-amber-500" /> (terminal)</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="offer_declined" label="Declined" />
                <span className="text-app-muted">You declined the offer — can undo</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="rejected" label="Rejected" />
                <span className="text-app-muted">Company rejected — can resume if reconsidered</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="ghosted" label="Ghosted" />
                <span className="text-app-muted">No response — can resume if they reply</span>
              </div>
              <div className="flex items-start gap-2">
                <StatusBadge status="withdrawn" label="Withdrawn" />
                <span className="text-app-muted">You withdrew (terminal)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Browser Extension */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-bold text-app-ink font-display">Browser Extension</h2>
        <p className="text-sm text-app-muted">
          Use the JobTrackr browser extension to save jobs directly from job boards with one click.
          The extension extracts job details automatically and saves them to your dashboard.
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href={EXTENSION_URLS.chrome}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-app-surface px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-surface transition-colors"
          >
            <Icon type="chrome" size={20} />
            Chrome Extension
          </a>
          <a
            href={EXTENSION_URLS.firefox}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-orange-600 dark:bg-orange-700 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 dark:hover:bg-orange-800 transition-colors"
          >
            <Icon type="firefox" size={20} />
            Firefox Add-on
          </a>
          <a
            href={EXTENSION_URLS.edge}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-app-primary px-4 py-2 text-sm font-medium text-white hover:bg-app-primary-hover transition-colors"
          >
            <Icon type="edge" size={20} />
            Edge Add-on
          </a>
        </div>
      </div>

      {/* Contact Support */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-bold text-app-ink font-display">Need more help?</h2>
        <p className="text-sm text-app-muted">
          Can't find what you're looking for? Send us feedback or report an issue.
        </p>
        <a
          href="mailto:contact@jobtrackr.it"
          className="inline-flex items-center gap-2 text-sm font-medium text-app-accent hover:text-app-accent-hover"
        >
          Contact support: contact@jobtrackr.it
        </a>
      </div>
    </PageLayout>
  )
}
